import classNames from 'classnames';
import React, {
  Suspense,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import AvatarStack from '../components/AvatarStack';
import DetailPreviewDescriptionContainer from '../components/DetailPreviewDescriptionContainer';
import renderHighlight from '../utils/search/renderHighlight';
import { deltaToText } from '../utils/text/deltaText';
import * as styles from './GroupDetailContent.module.scss';
import GroupIcon from './GroupIcon';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { useGetUser } from '../user/useAllUsers';

// Avoid circular dependency
const DeltaPreview = React.lazy(() => import('../components/DeltaPreview'));

const GROUP_DETAIL_CONTENT_GROUP_FRAGMENT = gql(`
  fragment GroupDetailContent_group on Group {
    id
    description
    name
    banner {
      original
    }
    owners {
      ...useGetUser_user
    }
    members {
      ...useGetUser_user
    }
  }
`);

const GROUP_DETAIL_CONTENT_PUBLIC_GROUP_FRAGMENT = gql(`
  fragment GroupDetailContent_publicGroup on PublicGroupFields {
    id
    name
    banner {
      original
    }
  }
`);

interface GroupDetailContentProps {
  group:
    | DocumentType<typeof GROUP_DETAIL_CONTENT_GROUP_FRAGMENT>
    | DocumentType<typeof GROUP_DETAIL_CONTENT_PUBLIC_GROUP_FRAGMENT>;
  numLines?: number;
  setMinHeight?: (minHeight: number) => void;
}

const TITLE_HEIGHT = 64;
const MIN_CONTENT_HEIGHT = 37;
const FOOTER_HEIGHT = 26;

const GroupDetailContent = ({
  group,
  numLines,
  setMinHeight,
}: GroupDetailContentProps) => {
  const getUser = useGetUser();
  const contentRef = useRef<HTMLDivElement>(null);
  const [isContentReady, setIsContentReady] = useState(false);

  const description = 'description' in group ? group.description : null;
  const hasContent = !!deltaToText(description);
  const bannerImage = group.banner?.original;
  const members = [
    ...('owners' in group ? (group?.owners ?? []) : []),
    ...('members' in group ? (group?.members ?? []) : []),
  ];

  useEffect(() => {
    if (setMinHeight && group) {
      const minHeight =
        (bannerImage ? parseInt(styles.bannerHeight, 10) : 0) +
        TITLE_HEIGHT +
        (hasContent ? MIN_CONTENT_HEIGHT : 0) +
        (members.length > 0 ? FOOTER_HEIGHT : 0);

      setMinHeight(minHeight);
    }
  }, [setMinHeight, members.length, group, bannerImage, hasContent]);

  useLayoutEffect(() => {
    if (contentRef.current) {
      const verifyContentReady = () => {
        if (contentRef?.current?.getBoundingClientRect().height) {
          setIsContentReady(true);
        }
      };
      const resizeObserver = new ResizeObserver(verifyContentReady);
      resizeObserver.observe(contentRef.current);
      verifyContentReady();

      return () => resizeObserver.disconnect();
    }
    return undefined;
  }, [group]);

  return (
    <div className={styles.container}>
      <div ref={contentRef} className={styles.content}>
        <div
          className={classNames(styles.headerContainer, {
            [styles.hasSeparator]: hasContent || members.length > 0,
          })}
        >
          {bannerImage && (
            <div
              className={styles.banner}
              style={{
                backgroundImage: `url(${bannerImage})`,
              }}
            />
          )}
          <div className={styles.titleContainer}>
            <div className={styles.titleIcon}>
              <GroupIcon />
            </div>
            <h4 className={styles.title}>{renderHighlight(group.name)}</h4>
          </div>
        </div>
        <DetailPreviewDescriptionContainer
          content={description}
          contentReady={isContentReady}
        >
          {hasContent && (
            <div className={styles.description}>
              <Suspense fallback={null}>
                <DeltaPreview
                  content={description}
                  maxLines={numLines}
                  typography="searchResult"
                />
              </Suspense>
            </div>
          )}
        </DetailPreviewDescriptionContainer>
        {members.length > 0 && (
          <div className={styles.members}>
            <AvatarStack users={members.map(getUser)} limit={7} />
          </div>
        )}
      </div>
    </div>
  );
};

export default GroupDetailContent;
